import React, { useCallback, useMemo } from "react"
import { Box, useTheme, Tabs, Tab, Button, List, Divider } from "@mui/material"
import _ from "lodash"
import { useNavigate } from "react-router-dom"

import { MotionDiv } from "components/motions"
import { Head } from "components/Head"
import { useTranslation } from "providers/i18n"
import { TransportationTabPanel } from "extensions/em-transportation/components/TransportationTabPanel"
import { useBusDetail } from "extensions/em-transportation/hooks/useBusDetail"
import { useExtension } from "hooks/extension/useExtension"
import { BusListItem } from "extensions/em-transportation/components/BusListItem"
import { LocationIcon } from "components/icons"

export const EmBusTransportationMobilePage = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { extensionConfig } = useExtension()
  const busConfig = useMemo(
    () => _.get(extensionConfig, "em-transportation.options"),
    [extensionConfig]
  )

  const inboundLocationLinks = useMemo(
    () => _.get(busConfig, "bus.inboundLocations", []),
    [busConfig]
  )
  const outboundLocationLinks = useMemo(
    () => _.get(busConfig, "bus.outboundLocations", []),
    [busConfig]
  )

  const {
    selectedTrafficType,
    changeTrafficType,
    trafficTypes,
    inbound,
    outbound,
  } = useBusDetail(inboundLocationLinks, outboundLocationLinks, busConfig)
  const { data: inboundData, locations: inboundLocations } = inbound
  const { data: outboundData, locations: outboundLocations } = outbound
  const theme = useTheme()
  const styleOverrides =
    theme.components[`EmBusTransportationPage`]?.styleOverrides || {}
  const {
    trafficTabs,
    trafficTab,
    trafficTabPanel,
    trafficTabContainer,
    divider,
    buttonWrapper,
    button,
    list,
  } = styleOverrides

  const onChangeTab = useCallback(
    (e, trafficType) => {
      changeTrafficType(trafficType)
    },
    [changeTrafficType]
  )
  const onClickBusLocation = useCallback(
    (id) => {
      navigate(`/maps/place/${id}`)
    },
    [navigate]
  )
  return (
    <>
      <Head title={t("Transportation (Bus)")} />
      <MotionDiv
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.15 }}
      >
        <Box sx={trafficTabContainer}>
          <Tabs
            sx={trafficTabs}
            value={selectedTrafficType}
            onChange={onChangeTab}
            variant="fullWidth"
          >
            <Tab
              sx={trafficTab}
              label={t("Inbound")}
              value={trafficTypes.INBOUND}
            />
            <Tab
              sx={trafficTab}
              label={t("Outbound")}
              value={trafficTypes.OUTBOUND}
            />
          </Tabs>
          <TransportationTabPanel
            sx={trafficTabPanel}
            value={selectedTrafficType}
            index={trafficTypes.INBOUND}
          >
            <Box sx={buttonWrapper}>
              {inboundLocations.length > 0 &&
                inboundLocations.map((data) => {
                  const { id, location, name } = data
                  return (
                    <Button
                      variant="contained"
                      key={id}
                      sx={button}
                      onClick={() => onClickBusLocation(location?.id)}
                      startIcon={<LocationIcon />}
                    >
                      {t(name)}
                    </Button>
                  )
                })}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <List sx={list}>
                {inboundData.length > 0 &&
                  inboundData.map((data) => {
                    const { title, destination, origin, id } = data
                    return (
                      <Box key={id}>
                        <BusListItem
                          title={title}
                          destination={destination}
                          origin={origin}
                        />
                        <Divider sx={divider} />
                      </Box>
                    )
                  })}
              </List>
            </Box>
          </TransportationTabPanel>
          <TransportationTabPanel
            sx={trafficTabPanel}
            value={selectedTrafficType}
            index={trafficTypes.OUTBOUND}
          >
            <Box sx={buttonWrapper}>
              {outboundLocations.length > 0 &&
                outboundLocations.map((data) => {
                  const { id, locationId, name } = data
                  return (
                    <Button
                      variant="contained"
                      key={id}
                      sx={button}
                      onClick={() => onClickBusLocation(locationId)}
                      startIcon={<LocationIcon />}
                    >
                      {t(name)}
                    </Button>
                  )
                })}
            </Box>
            <List sx={list}>
              {outboundData.length > 0 &&
                outboundData.map((data) => {
                  const { title, destination, origin, id } = data
                  return (
                    <Box key={id}>
                      <BusListItem
                        title={title}
                        destination={destination}
                        origin={origin}
                      />
                      <Divider sx={divider} />
                    </Box>
                  )
                })}
            </List>
          </TransportationTabPanel>
        </Box>
      </MotionDiv>
    </>
  )
}
